/**
 * The thing that stops one broken component becoming a blank white screen.
 *
 * React unmounts the whole tree when a render throws, so without this a single
 * bad field anywhere takes the entire app down mid-lesson. For a child that is
 * the worst failure there is: nothing on screen, nothing to tap, no idea what
 * happened to the lesson they were doing.
 *
 * It earned its place immediately. The recap panel read `payout.xp` from a
 * server response that was a deploy behind and had no `payout` — a shape the
 * types said could not happen, and could, because **the server can be older
 * than the client**. That is not an exotic case; it is every rolling deploy,
 * and every child whose browser is holding a cached bundle.
 *
 * So this is deliberately dumb: it catches, it says something small and warm,
 * and it offers the one action that always works. It does not retry, and it
 * does not try to explain.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { reportError } from './telemetry.js';

interface Props {
  readonly children: ReactNode;
  /**
   * Shown instead of the children. Defaults to a small apology.
   *
   * Passing `null` shows *nothing*, which is right where an apology would be
   * worse than an absence — a missing acorn count reads better than a sentence
   * where a number should be. That distinction is why this is checked with
   * `in` rather than `??`: `null ?? default` is the default, so a `??` here
   * would quietly ignore the one caller that wants silence.
   */
  readonly fallback?: ReactNode;
  /** What to call the thing that broke, for the log line. */
  readonly what?: string;
}

interface State {
  readonly broken: boolean;
}

export default class Boundary extends Component<Props, State> {
  override state: State = { broken: false };

  static getDerivedStateFromError(): State {
    return { broken: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    const what = this.props.what ?? 'something';
    // The console, for whoever is looking at one. Nothing about the child goes
    // in it — the message and the component stack are about the code.
    console.error(`capytalk: ${what} broke`, error.message, info.componentStack);

    // And the server, so a failure on a device nobody is watching is still
    // visible ([Telemetry](../../../docs/TELEMETRY.md)). The **message only**:
    // a component stack is developer detail that cannot be scrubbed
    // meaningfully, and `what` already says where it happened.
    reportError({ code: 'render', screen: what, message: error.message });
  }

  override render(): ReactNode {
    if (!this.state.broken) return this.props.children;
    if ('fallback' in this.props) return this.props.fallback;
    return (
      <p className="tiny" role="status" style={{ textAlign: 'center' }}>
        There is something here I could not show.
      </p>
    );
  }
}
